import { RefObject, useLayoutEffect } from "react";

// Smallest font scale a text field may shrink to before its words are
// allowed to break mid-word instead.
const MIN_FIT_SCALE = 0.4;
const FIT_SEARCH_STEPS = 8;
// Sub-pixel rounding slack when comparing measured sizes.
const FIT_TOLERANCE_PX = 0.5;

/**
 * Shrinks a label text field until it fits its box, by measuring the rendered
 * text rather than estimating glyph widths (an estimate either clipped long
 * names or broke words like "Amortecedores" mid-word, and left short names
 * smaller than they needed to be).
 *
 * The text's font sizes are written as `calc(… * var(--label-fit, 1))`; this
 * sets `--label-fit` on `ref` to the largest scale (≤ 1) at which the content
 * is no taller than `maxHeightPercent` of the label and no word is wider than
 * the box. Only when even the minimum scale overflows does it let words break
 * anywhere (`--label-wrap`). The label is the nearest `[data-label-root]`
 * ancestor; measurements are layout sizes, so they're the same in the editor
 * canvas, a scaled-down preview card and the print copy.
 */
export const useFitText = (
  ref: RefObject<HTMLElement>,
  maxHeightPercent: number,
  // Anything that changes the text's natural size: content, font, box.
  contentKey: string,
  // Smallest scale allowed (e.g. so text never goes below the SKU's size on
  // small labels); below it the text keeps this size and wraps anywhere.
  minScale: number = MIN_FIT_SCALE,
) => {
  useLayoutEffect(() => {
    const node = ref.current;
    const labelRoot = node?.closest<HTMLElement>("[data-label-root]");

    if (!node || !labelRoot) {
      return;
    }

    const floor = Math.min(1, Math.max(MIN_FIT_SCALE, minScale));

    const fit = () => {
      const maxHeight = (labelRoot.clientHeight * maxHeightPercent) / 100;
      const maxWidth = node.clientWidth;

      if (!maxHeight || !maxWidth) {
        return;
      }

      const fitsAt = (scale: number) => {
        node.style.setProperty("--label-fit", String(scale));

        return (
          node.scrollHeight <= maxHeight + FIT_TOLERANCE_PX &&
          node.scrollWidth <= maxWidth + FIT_TOLERANCE_PX
        );
      };

      node.style.setProperty("--label-wrap", "normal");

      if (fitsAt(1)) {
        return;
      }

      if (!fitsAt(floor)) {
        node.style.setProperty("--label-wrap", "anywhere");
        fitsAt(floor);

        return;
      }

      let fitting = floor;
      let overflowing = 1;

      for (let step = 0; step < FIT_SEARCH_STEPS; step += 1) {
        const middle = (fitting + overflowing) / 2;

        if (fitsAt(middle)) {
          fitting = middle;
        } else {
          overflowing = middle;
        }
      }

      fitsAt(fitting);
    };

    fit();

    // Web fonts finishing loading, or the label itself resizing (editor
    // canvas, preview column width), change where the text wraps.
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(fit);

    observer.observe(labelRoot);

    return () => observer.disconnect();
  }, [ref, maxHeightPercent, contentKey, minScale]);
};
